import axios from "axios";
import { API_BASE } from "../config/api";

/*
EstateCoin API Client
Ensures authentication cookies are sent with every request.
In-memory GET cache for stable endpoints to reduce latency.
*/

const CACHE_TTL_MS = {
  "/api/properties": 30000,
  "/api/system/status": 10000,
  "/api/public/stats": 60000,
};

const memCache = new Map();

function buildFullUrl(url, baseURL) {
  const base = (baseURL || "").replace(/\/$/, "");
  const path = (url || "").startsWith("/") ? url : `/${url || ""}`;
  return base + path;
}

const instance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

instance.interceptors.request.use((config) => {
  const method = (config.method || "get").toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    if (!config.headers["Idempotency-Key"]) {
      config.headers["Idempotency-Key"] =
        typeof crypto !== "undefined" && crypto?.randomUUID
          ? crypto.randomUUID()
          : Date.now().toString() + Math.random().toString(36);
    }
  }
  // Auth via httpOnly cookies — withCredentials: true sends them automatically
  return config;
});

instance.interceptors.response.use(
  (response) => {
    if ((response.config?.method || "get").toUpperCase() !== "GET") return response;
    const fullUrl = buildFullUrl(response.config.url, response.config.baseURL || instance.defaults.baseURL);
    for (const path of Object.keys(CACHE_TTL_MS)) {
      if (fullUrl.includes(path)) {
        memCache.set(fullUrl, { data: response.data, timestamp: Date.now() });
        break;
      }
    }
    return response;
  },
  (error) => {
    if (error?.response?.status === 401 && !window.location.pathname.includes("/login") && error?.config?.url?.includes("/api/auth/me")) {
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

const originalGet = instance.get.bind(instance);
instance.get = function (url, config = {}) {
  const fullUrl = buildFullUrl(url, config.baseURL || instance.defaults.baseURL);
  for (const [path, ttl] of Object.entries(CACHE_TTL_MS)) {
    if (fullUrl.includes(path)) {
      const cached = memCache.get(fullUrl);
      if (cached && Date.now() - cached.timestamp < ttl) {
        return Promise.resolve({
          data: cached.data,
          status: 200,
          config: { url, ...config },
          headers: {},
        });
      }
      break;
    }
  }
  return originalGet(url, config);
};

export function clearApiCache() {
  memCache.clear();
}

const apiClient = instance;
export default apiClient;
